import { UzButton } from '@utazzo/ui';
import { ArrowLeft } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AddressesStep } from '../components/AddressesStep';
import { ContactsStep } from '../components/ContactsStep';
import { STEPS, CustomerStepperHeader, CustomerStepperNav } from '../components/CustomerStepper';
import { DocumentsStep } from '../components/DocumentsStep';
import { PROFILE_SUBMIT_ID, ProfileStep } from '../components/ProfileStep';
import { useCustomer } from '../use-customers';

import { PageShell } from '@/components/page-shell';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Full-page stepper for creating and editing a customer.
 *
 * ID-first design: when creating a new customer the Profile step (step 0)
 * must POST the customer record before steps 1-3 can attach sub-resources
 * (addresses, contacts, documents all need the :id in their URL). Once the
 * customer exists, all four steps are immediately available and the form
 * stays on the step the user was last on.
 *
 * For edit mode (id in URL params), the customer detail is loaded from the
 * cache and all steps are unlocked immediately.
 */
export const CustomerStepperPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const backToList = useListReturn('/customers');

  // The resolved customer id. Starts null for "new" flows; populated after
  // step 0 creates the record or when editing an existing customer.
  const [resolvedId, setResolvedId] = useState<string | null>(isNew ? null : (id ?? null));
  const [currentStep, setCurrentStep] = useState(0);

  // Editing an existing customer - fetch the full detail (addresses, contacts, documents).
  const customerQuery = useCustomer(resolvedId);
  const customer = customerQuery.data ?? null;

  // Whether step 0's form mutations are in flight.
  const [profilePending, setProfilePending] = useState(false);

  // What to do once the Profile step saves: advance to the next step
  // ("Save & continue") or return to the list ("Save & close"). Set just
  // before the hidden submit is fired so the save handler knows the intent.
  const saveIntentRef = useRef<'continue' | 'close'>('continue');

  const handleProfileSaved = useCallback(
    (savedId: string) => {
      setResolvedId(savedId);
      if (saveIntentRef.current === 'close') {
        backToList();
      } else {
        setCurrentStep(1);
      }
    },
    [backToList],
  );

  const submitProfile = useCallback((intent: 'continue' | 'close') => {
    saveIntentRef.current = intent;
    // Trigger the hidden submit button inside ProfileStep's UzForm.
    const btn = document.getElementById(PROFILE_SUBMIT_ID) as HTMLButtonElement | null;
    btn?.click();
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep === 0) {
      submitProfile('continue');
    } else {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  }, [currentStep, submitProfile]);

  const handleSaveAndClose = useCallback(() => {
    submitProfile('close');
  }, [submitProfile]);

  const handleBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }, []);

  const handleDone = useCallback(() => {
    backToList();
  }, [backToList]);

  const pageTitle = isNew
    ? 'New customer'
    : customer
      ? `Edit - ${customer.displayName}`
      : 'Edit customer';

  return (
    <PageShell
      title={pageTitle}
      description={STEPS[currentStep]?.description}
      actions={
        <UzButton variant="ghost" size="sm" onClick={backToList}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to list
        </UzButton>
      }
    >
      <div className="mx-auto max-w-5xl min-w-0 space-y-6 overflow-x-hidden">
        {/* Step indicator */}
        <CustomerStepperHeader currentStep={currentStep} customerId={resolvedId} />

        {/* Active step content - only the current step is mounted */}
        <div className="min-h-[300px] min-w-0">
          {currentStep === 0 && (
            <ProfileStep
              customer={customer}
              onSaved={(savedId) => {
                setProfilePending(false);
                handleProfileSaved(savedId);
              }}
            />
          )}
          {currentStep === 1 && resolvedId && (
            <AddressesStep customerId={resolvedId} addresses={customer?.addresses ?? []} />
          )}
          {currentStep === 2 && resolvedId && (
            <ContactsStep customerId={resolvedId} contacts={customer?.contacts ?? []} />
          )}
          {currentStep === 3 && resolvedId && (
            <DocumentsStep customerId={resolvedId} documents={customer?.documents ?? []} />
          )}
          {/* Guard: if steps 1-3 are reached without an id (shouldn't happen
              in normal flow but handles a stale URL), prompt to save profile first. */}
          {currentStep > 0 && !resolvedId && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-zinc-500">
              <p>Please save the Profile step first to unlock this step.</p>
              <UzButton variant="ghost" onClick={() => setCurrentStep(0)}>
                Go to Profile
              </UzButton>
            </div>
          )}
        </div>

        {/* Navigation controls */}
        <CustomerStepperNav
          currentStep={currentStep}
          totalSteps={STEPS.length}
          onBack={handleBack}
          onNext={handleNext}
          onDone={handleDone}
          onSaveAndClose={handleSaveAndClose}
          isSaving={profilePending || customerQuery.isFetching}
          customerId={resolvedId}
        />
      </div>
    </PageShell>
  );
};
